/* RA Boutique: collection layout analytics, v1. No visitor identifiers or storage. */
(() => {
  'use strict';
  if (window.RACollectionAnalytics) return;
  const configNode = document.getElementById('ra-collection-analytics-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const preview = config.preview === true || window.Shopify?.designMode === true;
  const gridSelector = '#CollectionProductGrid [data-product-container]';
  const switchSelector = '.m-toolbar--column-switcher button';
  const pendingAdds = new Map();
  let grid = null;
  let last = null;
  let ready = false;
  let timer;
  let reason = 'initial';
  let selectedLayout = null;
  let resizing = false;
  let quickviewProduct = null;
  let privacyLoading = false;
  let subscribed = false;
  let initialPublished = false;

  const permitted = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  };
  const canSample = () => preview || permitted();
  const status = value => { configNode.dataset.raAnalyticsStatus = value; };
  const device = () => window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop';

  function view() {
    const container = document.querySelector(gridSelector);
    if (!container || !container.querySelector('.m-product-card')) return null;
    const viewport = device();
    if (viewport === 'mobile' && container.classList.contains('m-layout-editorial')) {
      return { layout: 'editorial', viewport_class: viewport };
    }
    const columns = [...container.classList].find(value => /^m-cols-[1-6]$/.test(value));
    if (!columns) return null;
    return { layout: `${columns.slice(-1)}_columns`, viewport_class: viewport };
  }

  function emit(name, values) {
    if (!canSample()) return false;
    const data = {
      schema_version: 1,
      collection_id: String(config.collection_id),
      collection_handle: config.collection_handle,
      ...values
    };
    // Draft/editor checks are local diagnostics, never customer analytics.
    if (preview) {
      status('preview_only');
      configNode.dataset.raLastEvent = JSON.stringify({ name, data });
      configNode.dataset.raEventCount = String(Number(configNode.dataset.raEventCount || 0) + 1);
      return true;
    }
    if (typeof window.Shopify?.analytics?.publish !== 'function') {
      status('waiting_for_shopify');
      return false;
    }
    try {
      const result = window.Shopify.analytics.publish(`ra_boutique:${name}`, data);
      if (result === false) { status('publish_failed'); return false; }
      if (result && typeof result.catch === 'function') result.catch(() => status('publish_failed'));
      status('events_published');
      return true;
    } catch (_) { status('publish_failed'); return false; }
  }

  function sample(changeReason = 'layout_update') {
    if (!ready || document.visibilityState === 'hidden') return;
    if (!canSample()) { status('waiting_for_consent'); return; }
    const current = view();
    if (!current) return;
    if (!initialPublished) {
      if (emit('collection_view', { ...current, change_reason: changeReason })) {
        initialPublished = true;
        last = current;
      }
      return;
    }
    if (current.layout === last.layout && current.viewport_class === last.viewport_class) {
      selectedLayout = null;
      resizing = false;
      return;
    }
    const cause = current.layout === selectedLayout ? 'selection'
      : resizing || current.viewport_class !== last.viewport_class ? 'viewport' : changeReason;
    if (emit('collection_layout_change', { ...current, previous_layout: last.layout, change_reason: cause })) {
      last = current;
      selectedLayout = null;
      resizing = false;
    }
  }

  function schedule(nextReason = 'layout_update', delay = 100) {
    reason = nextReason;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => sample(reason), delay);
  }

  const gridObserver = new MutationObserver(() => schedule());
  function bindGrid() {
    const current = document.querySelector(gridSelector);
    if (current === grid) return;
    gridObserver.disconnect();
    grid = current;
    if (grid) gridObserver.observe(grid, { attributes: true, attributeFilter: ['class'] });
    schedule();
  }

  // Match only direct catalogue items, excluding inserted lookbooks/videos and recommendations.
  function catalogueCard(target) {
    const card = target?.closest?.('.m-product-card');
    const item = card?.closest('.m-product-item');
    if (!item || item.parentElement !== document.querySelector(gridSelector)) return null;
    if (item.matches('[data-ra-look-insert], [data-ra-arrivals-grid-video], [data-ra-code-extension-grid-video]')) return null;
    return /^\d+$/.test(card.dataset.productId || '') ? card : null;
  }

  document.addEventListener('click', event => {
    const target = event.target;
    const button = target?.closest?.(switchSelector);
    if (button?.closest('#CollectionProductGrid')) {
      if (!button.hasAttribute('data-column') && !button.hasAttribute('data-ra-editorial-view')) return;
      sample();
      selectedLayout = button.hasAttribute('data-ra-editorial-view') ? 'editorial' : `${button.dataset.column}_columns`;
      schedule('selection', 60);
      return;
    }
    if (!canSample()) return;
    const quickview = target?.closest?.('.m-product-quickview-button');
    // Clear stale attribution when another quick view (e.g. a look) is opened.
    if (quickview) quickviewProduct = null;
    const card = catalogueCard(target);
    if (!card) return;
    const link = target?.closest?.('a[href]');
    let productLink = false;
    if (link) {
      try { productLink = /\/products\/[^/]+/.test(new URL(link.href, window.location.origin).pathname); } catch (_) {}
    }
    if (!quickview && !productLink) return;
    const current = view();
    if (!current) return;
    sample();
    if (quickview) quickviewProduct = card.dataset.productId;
    emit('collection_product_click', {
      ...current,
      product_id: card.dataset.productId,
      interaction_kind: quickview ? 'quick_view' : 'product_page'
    });
  }, true);

  document.addEventListener('submit', event => {
    if (!canSample()) return;
    const form = event.target;
    if (!form?.matches?.('form') || !form.closest('product-form') || form.closest('product-form')._raAddPending) return;
    const card = catalogueCard(form);
    const formProduct = form.closest('[data-product-id]')?.dataset.productId;
    const inQuickview = form.closest('.m-product-quickview');
    const productId = card?.dataset.productId || (inQuickview && formProduct === quickviewProduct ? formProduct : null);
    const variantId = form.querySelector('[name="id"]')?.value;
    if (!productId || !/^\d+$/.test(variantId || '')) return;
    const current = view();
    if (!current) return;
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
    if (!Number.isInteger(quantity) || quantity < 1) return;
    for (const [key, pending] of pendingAdds) if (pending.expires < Date.now()) pendingAdds.delete(key);
    pendingAdds.set(String(variantId), { ...current, product_id: productId, quantity, expires: Date.now() + 30000 });
  }, true);

  function subscribeCart() {
    if (subscribed || !window.MinimogEvents?.subscribe || !window.MinimogTheme?.pubSubEvents) return;
    subscribed = true;
    window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartError, error => {
      pendingAdds.delete(String(error?.productVariantId));
    });
    window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, item => {
      // This theme publishes the cart/add response for successful product forms.
      // Cart edits publish source=main-cart-items and must never count as additions.
      if (!item || item.source || !item.variant_id || !item.product_id || item.status) return;
      const key = String(item.variant_id);
      const pending = pendingAdds.get(key);
      pendingAdds.delete(key);
      if (!pending || Date.now() > pending.expires || String(item.product_id) !== pending.product_id) return;
      const { expires, ...data } = pending;
      emit('collection_add_to_cart', data);
    });
  }

  function consentChanged() {
    pendingAdds.clear();
    quickviewProduct = null;
    selectedLayout = null;
    if (!canSample()) { status('waiting_for_consent'); return; }
    schedule('consent');
  }
  function loadPrivacy() {
    if (preview || permitted()) { schedule('initial'); return; }
    status('waiting_for_consent');
    if (window.Shopify?.customerPrivacy || privacyLoading || !window.Shopify?.loadFeatures) return;
    privacyLoading = true;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      privacyLoading = false;
      if (!error) schedule('initial');
    });
  }

  const rootObserver = new MutationObserver(bindGrid);
  function start() {
    ready = true;
    bindGrid();
    const root = document.getElementById('MainContent');
    if (root) rootObserver.observe(root, { childList: true, subtree: true });
    subscribeCart();
    loadPrivacy();
    // Bounded retries cover Shopify's deferred initialization without replaying clicks.
    [1000, 3000, 8000].forEach(delay => window.setTimeout(() => { subscribeCart(); loadPrivacy(); }, delay));
  }
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('trackingConsentAccepted', consentChanged);
  document.addEventListener('visibilitychange', () => schedule('visibility'));
  window.addEventListener('resize', () => { resizing = true; schedule('viewport', 600); });
  window.addEventListener('pageshow', event => { if (event.persisted) schedule('back_navigation'); });
  window.RACollectionAnalytics = { version: 1 };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
